import { useMemo } from "react";
import { batchMasterApi } from "../../api/batchMasterApi";
import { useApiQuery } from "../../hooks/useApiQuery";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";
import { matchesMachine } from "./analyticsFilters";

const EMPTY = [];

/** Fetch every master-data page so machines and filters cover the whole plant. */
export default function useAnalyticsFilterOptions(filters = {}) {
  const { selectedPlantId } = usePlantSelection();
  const query = useApiQuery(async () => {
    const [lines, machines, shifts] = await Promise.all([
      batchMasterApi.lines(selectedPlantId), batchMasterApi.machines(selectedPlantId), batchMasterApi.shifts(selectedPlantId),
    ]);
    return { lines, machines, shifts, plantId: selectedPlantId };
  }, [selectedPlantId]);
  const records = query.data?.plantId === selectedPlantId ? query.data : null;
  const machines = records?.machines || EMPTY;
  const shifts = records?.shifts || EMPTY;

  return {
    lineOptions: useMemo(() => (records?.lines || EMPTY)
      .filter((line) => !selectedPlantId || line.plant_id === selectedPlantId)
      .map((line) => ({ value: line.line_id, label: line.line_name || line.line_id })), [records?.lines, selectedPlantId]),
    machineOptions: useMemo(() => machines
      .filter((machine) => matchesMachine(machine, { line: filters.line, machineType: filters.machineType }, selectedPlantId))
      .map((machine) => ({ value: machine.machine_id, label: machine.machine_name || machine.machine_id })), [machines, filters.line, filters.machineType, selectedPlantId]),
    machineTypeOptions: useMemo(() => [...new Set(machines
      .filter((machine) => !selectedPlantId || machine.plant_id === selectedPlantId)
      .map((machine) => machine.relation_type || machine.machine_type).filter(Boolean))]
      .map((type) => ({ value: type, label: type })), [machines, selectedPlantId]),
    shiftOptions: useMemo(() => shifts.filter((shift) => !selectedPlantId || shift.plant_id === selectedPlantId)
      .map((shift) => ({ value: shift.shift_id, label: shift.shift_name || shift.shift_id })), [shifts, selectedPlantId]),
    machines,
    shifts,
    isLoading: query.isLoading || (!records && !query.error),
    error: query.error,
    refetch: query.refetch,
  };
}
